// Servicio para el chat interno (persistencia en backend).
// Usamos un cliente sin interceptor 401 para no cerrar sesión si falla el chat.
import axios from 'axios';

const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';

const getClient = () => {
    const token = localStorage.getItem('token');
    return axios.create({
        baseURL,
        headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
    });
};

const getClientMultipart = () => {
    const token = localStorage.getItem('token');
    return axios.create({
        baseURL,
        headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
    });
};

/** Lista todas las conversaciones del usuario (privados + grupos). El frontend las separa en destacados/grupales/privados. */
const getConversaciones = () => {
    return getClient().get('/chat-interno/conversaciones').catch(() => ({ data: [] }));
};

/** Crear o obtener conversación privada con otro usuario. Devuelve ConversacionInternoDto. */
const crearConversacionPrivada = (idUsuarioDestino) => {
    return getClient().post('/chat-interno/conversaciones', { idUsuarioDestino });
};

/** Crear conversación grupal (opcional). */
const crearConversacionGrupal = (nombreGrupo, idParticipantes) => {
    return getClient().post('/chat-interno/conversaciones', { nombreGrupo, idParticipantes });
};

/** Mensajes de una conversación (paginados). */
const getMensajes = (idConversacion, page = 0, size = 100) => {
    return getClient().get(`/chat-interno/conversaciones/${idConversacion}/mensajes`, { params: { page, size } });
};

/** Enviar mensaje: body { tipo, contenidoTexto?, urlImagen?, metadataJson? }. */
const enviarMensaje = (idConversacion, body) => {
    return getClient().post(`/chat-interno/conversaciones/${idConversacion}/mensajes`, body);
};

/** Obtener detalle de una conversación (participantes, nombre grupo, etc.). */
const getConversacion = (idConversacion) => {
    return getClient().get(`/chat-interno/conversaciones/${idConversacion}`);
};

/** Marcar o desmarcar conversación como destacada. */
const marcarDestacado = (idConversacion, destacado = true) => {
    return getClient().patch(`/chat-interno/conversaciones/${idConversacion}/destacar`, null, { params: { destacado } });
};

/** Actualizar grupo (nombre). */
const actualizarGrupo = (idConversacion, body) => {
    return getClient().patch(`/chat-interno/conversaciones/${idConversacion}`, body);
};

/** Agregar participante al grupo. */
const agregarParticipante = (idConversacion, idUsuario) => {
    return getClient().post(`/chat-interno/conversaciones/${idConversacion}/participantes`, { idUsuario });
};

/** Quitar participante del grupo. */
const quitarParticipante = (idConversacion, idUsuario) => {
    return getClient().delete(`/chat-interno/conversaciones/${idConversacion}/participantes/${idUsuario}`);
};

/** Eliminar conversación (grupo o privado). */
const eliminarConversacion = (idConversacion) => {
    return getClient().delete(`/chat-interno/conversaciones/${idConversacion}`);
};

/** Subir imagen (multipart). Devuelve { url }. */
const subirAdjunto = (file) => {
    const form = new FormData();
    form.append('file', file);
    return getClientMultipart().post('/chat-interno/adjuntos', form);
};

/** Lista de usuarios del sistema para buscar y enviar mensaje. */
const getUsuariosSistema = () => {
    return getClient().get('/usuarios/para-chat').catch(() => ({ data: [] }));
};

/** Total de mensajes no leídos en el chat interno (para badge). */
const getTotalNoLeidos = () => {
    return getClient().get('/chat-interno/total-no-leidos').then((r) => r.data?.total ?? 0).catch(() => 0);
};

/** Marcar conversación como leída (resta del total del badge). */
const marcarComoLeido = (idConversacion) => {
    return getClient().patch(`/chat-interno/conversaciones/${idConversacion}/leer`).catch(() => {});
};

const chatInternoService = {
    getConversaciones,
    getConversacion,
    crearConversacionPrivada,
    crearConversacionGrupal,
    getMensajes,
    enviarMensaje,
    marcarDestacado,
    actualizarGrupo,
    agregarParticipante,
    quitarParticipante,
    eliminarConversacion,
    subirAdjunto,
    getUsuariosSistema,
    getTotalNoLeidos,
    marcarComoLeido,
};

export default chatInternoService;

